import {ChangeDetectorRef, Component, computed, inject, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {NgClass} from '@angular/common';
import {TitlePagesComponent} from '@/app/shared/components/title-pages-component/title-pages-component';
import {AppConstants} from '@/app/constants/AppConstants';
import {ModalCargaComponent} from '@/app/shared/components/modal-carga-component/modal-carga-component';
import {CampanasService} from '../../services/campanas.service';
import {SnackbarService} from '@/app/shared/services/snackbar.service';
import {Campaign} from '../../services/campaign.interface';
import {saveAs} from 'file-saver-es';
import {CampaingsStateService} from '../../services/campaing.state.service';
import {ClientStore} from '@/app/core/states/clientes-state/client-state.store';
import {Client} from '../../../cliente/models/cliente.interface';
import * as XLSX from 'xlsx';
import {ModalAddTokensComponent} from '@/app/features/campañas/component/modal-add-tokens.component';
import {ThemeService} from '@/app/core/services/theme/theme.service';
import {SurveyResponsesService} from '@/app/features/Resultados/services/survey-responses.service';
import {forkJoin, map, of, switchMap} from 'rxjs';


// Nueva función validadora para la fecha de inicio
const futureDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const raw = control.value;
  if (!raw) return null;

  let selected: Date;
  if (typeof raw === 'string') {
    const [y, m, d] = raw.split('T')[0].split('-').map(Number);
    selected = new Date(y, (m ?? 1) - 1, d ?? 1); // local 00:00
  } else if (raw instanceof Date) {
    selected = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  } else {
    const d = new Date(raw);
    selected = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  const today = toDateOnly(new Date());
  return selected <= today ? {futureDate: true} : null;
};

// Convierte a fecha local sin hora (Y/M/D 00:00)
const toDateOnly = (input: string | Date): Date => {
  if (!input) return new Date(NaN);
  if (typeof input === 'string') {
    // soporta 'YYYY-MM-DD' y 'YYYY-MM-DDTHH:mm:ss'
    const [y, m, d] = input.split('T')[0].split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }
  return new Date(input.getFullYear(), input.getMonth(), input.getDate());
};

@Component({
  selector: 'app-campanas-form-component',
  standalone: true,
  imports: [
    TitlePagesComponent,
    ReactiveFormsModule,
    NgClass,
    ModalCargaComponent,
    RouterLink,
    ModalAddTokensComponent
  ],
  templateUrl: './campanas-form-component.html',
  styleUrl: './campanas-form-component.scss',

})
export class CampanasFormComponent {
  private themeService = inject(ThemeService);
  private surveyResponses = inject(SurveyResponsesService);

  protected tokensContestados = new Set<string>();

  protected themeContent = this.themeService.themeContent;
  protected campanasFormData = computed(() => this.themeContent()?.campaignsFormComponent);

  isTokenView = false;
  protected showEmptyTokensModal = false;
  isLocked = false;
  columnDefinitions = [
    {key: 'token', displayName: 'Token'},
  ];
  protected readonly AppConstants = AppConstants;
  //para los formularios
  protected step!: number;
  protected submitted = false;
  protected startDateFocused = true;
  protected endDateFocused = true;
  protected modeEditCampaing!: boolean;
  //datos que se pasan al header
  pageData = signal<{
    title: string,
    subTitle: string
  }>({
    title: '',
    subTitle: ''
  });
  //muestra el modal de carga
  protected isLoading!: boolean;
  // se usa para validar si se obtuvieron los tokens
  protected stepGeneratedTokens!: boolean;
  protected tokens: any[] = [];
  protected flujoCompletado = false;
  //se usa para obtener el id de la campaña para generar los tokens
  protected campaing!: Campaign;
  protected campaingToEdit: Campaign | null = null;
  protected editingMode: boolean = false;
  protected canGenerateTokens = false;
  // dentro de la clase CampanasFormComponent
  protected minStartDate = ''; // YYYY-MM-DD
  private changeDetectorRef = inject(ChangeDetectorRef);
  private formBuilder = inject(FormBuilder);
  formGenerateTokens = this.formBuilder.group({
    nameCampain: new FormControl({value: '', disabled: true}),
    numberOfTokens: new FormControl(0),
  });
  private router = inject(Router);
  private campanasService = inject(CampanasService);
  private snackbarService = inject(SnackbarService);
  private campanasStateService = inject(CampaingsStateService);
  private store = inject(ClientStore);
  private client!: Client | null;
  private route = inject(ActivatedRoute);

  get step2(): boolean {
    return this.step >= 2;
  }

  get name() {
    return this.formCampaing.get('name');
  }

  get description() {
    return this.formCampaing.get('description');
  }

  get startDate() {
    return this.formCampaing.get('startDate');
  }

  get endDate() {
    return this.formCampaing.get('endDate');
  }

  get totalEmployees() {
    return this.formCampaing.get('totalEmployees');
  }

  get tokensToGenerate() {
    return this.formCampaing.get('tokensToGenerate');
  }

  get showCompletionBanner(): boolean {
    return this.flujoCompletado && !this.campaingToEdit;
  }

  ngOnInit(): void {
    const c = this.store.client
    this.client = c();
    this.campaingToEdit = this.campanasStateService.getCampaign();
    this.setHeader();
    this.step = 1;
    this.addFormValidations();

    const viewFromData = this.route.snapshot.data?.['view'] as string | undefined;
    const viewFromQuery = this.route.snapshot.queryParamMap.get('view');
    this.isTokenView = (viewFromData ?? viewFromQuery) === 'tokens';

    if (this.campaingToEdit && this.isTokenView) {
      this.getTokens();
    }

    this.minStartDate = this.toISODate(this.addDays(new Date(), 1));
    this.startDate?.addValidators(futureDateValidator);
    this.startDate?.updateValueAndValidity({emitEvent: false});
  }

  ngOnDestroy(): void {
    this.campanasStateService.clearCampaign();
  }

  //se utiliza para cambiar el titulo de las pantallas
  setHeader() {
    if (this.campaingToEdit !== null && this.campaingToEdit !== undefined) {
      this.loadCampaing();
      this.pageData.set({
        title: AppConstants.CAMPANAS_FORM_TITLE_EDIT,
        subTitle: AppConstants.CAMPANAS_FORM_SUBTITLE_EDIT
      });
      this.modeEditCampaing = true;
    } else {
      this.pageData.set({
        title: this.campanasFormData()?.createCampaignTitle || AppConstants.CAMPANAS_FORM_TITLE_CREATE,
        subTitle: this.campanasFormData()?.createCampaignDescription || AppConstants.CAMPANAS_FORM_SUBTITLE_CREATE
      });
    }
  }

  //para el envio de formularios
  onSubmit() {
    this.submitted = true;
    if (this.isLocked) return;
    if (this.formCampaing.invalid) {
      return;
    }

    let campanaData = {
      clientPublicId: this.client?.publicId ?? "",
      name: this.formCampaing.value.name?.trim() ?? '',
      description: this.formCampaing.value.description?.trim() ?? '',
      surveyUrl: this.formCampaing.value.surveyUrl?.trim() ?? '',
      startDate: this.formCampaing.value.startDate + "T09:00:00",
      endDate: this.formCampaing.value.endDate + "T23:59:59",
      totalEmployees: Number(this.formCampaing.value.totalEmployees),
      minimumResponses: Number(this.formCampaing.get('minimumResponses')?.value),
      tokensToGenerate: Number(this.formCampaing.value.tokensToGenerate)
    };

    if (this.campaingToEdit !== null) {
      this.campanasService.updateCampaign(this.campaingToEdit.publicId, campanaData)
        .subscribe({
          next: () => {
            this.snackbarService.showSuccess('Campaña editada con éxito');
            this.formCampaing.reset();
            this.router.navigate(['/clientes/campañas']);
          },
          error: (err) => {
            this.snackbarService.showError('Error al editar la campaña ' + err.message);
          }
        });
    } else {
      this.campanasService.createCampaign(campanaData).subscribe({
        next: (createdCampaign: Campaign) => {
          this.campaing = createdCampaign;
          this.snackbarService.showSuccess('Campaña creada con éxito ');
          this.formCampaing.reset();
          this.step = 2;
          this.updateHeader();
          this.formGenerateTokens.patchValue({
            nameCampain: this.campaing.name,
            numberOfTokens: this.campaing.tokensToGenerate
          });

          const totalEmployees = this.campaing.totalEmployees;
          const tokensToGenerate = this.campaing.tokensToGenerate;
          const numberOfTokensControl = this.formGenerateTokens.get('numberOfTokens');

          if (numberOfTokensControl) {
            if (tokensToGenerate < totalEmployees) {
              numberOfTokensControl.enable();
              numberOfTokensControl.setValidators([
                Validators.required,
                Validators.min(tokensToGenerate),
                Validators.max(totalEmployees)
              ]);
              numberOfTokensControl.updateValueAndValidity();
            } else {
              numberOfTokensControl.disable();
            }
          }
        },
        error: (err) => {
          console.log(err)
          this.snackbarService.showError('Error al crear la campaña ' + err.message);
        }
      });
    }
  }

  loadCampaing() {
    if (this.campaingToEdit === null) return;

    this.editingMode = true;

    const startDateOnly = toDateOnly(this.campaingToEdit.startDate);
    const endDateOnly = toDateOnly(this.campaingToEdit.endDate);
    const today = toDateOnly(new Date());

    // Regla: si ya comenzó (hoy >= start) o ya terminó (hoy > end) = bloqueado
    this.isLocked = (today >= startDateOnly) || (today > endDateOnly);
    this.canGenerateTokens = !this.isLocked;

    // Valores al formulario (en formato YYYY-MM-DD)
    this.formCampaing.patchValue({
      name: this.campaingToEdit.name,
      description: this.campaingToEdit.description,
      surveyUrl: this.campaingToEdit.surveyUrl,
      startDate: this.campaingToEdit.startDate.split('T')[0],
      endDate: this.campaingToEdit.endDate.split('T')[0],
      totalEmployees: this.campaingToEdit.totalEmployees.toString(),
      minimumResponses: this.campaingToEdit.minimumResponses,
      tokensToGenerate: this.campaingToEdit.tokensToGenerate.toString()
    }, {emitEvent: false});

    // Estos 3 campos NO se pueden editar NUNCA en edición
    this.formCampaing.get('totalEmployees')?.disable({emitEvent: false});
    this.formCampaing.get('minimumResponses')?.disable({emitEvent: false});
    this.formCampaing.get('tokensToGenerate')?.disable({emitEvent: false});

    if (this.isLocked) {
      // Ya comenzó o terminó → no se edita NADA
      this.formCampaing.disable({emitEvent: false});
    } else {
      // Aún no inicia → se puede editar: nombre, descripción, url, startDate y endDate
      this.formCampaing.enable({emitEvent: false});

      // bloquea los 3 que nunca van
      this.formCampaing.get('totalEmployees')?.disable({emitEvent: false});
      this.formCampaing.get('minimumResponses')?.disable({emitEvent: false});
      this.formCampaing.get('tokensToGenerate')?.disable({emitEvent: false});

      // Regla: startDate > hoy (al menos mañana)
      this.formCampaing.get('startDate')?.setValidators([Validators.required, futureDateValidator]);
      this.formCampaing.get('startDate')?.updateValueAndValidity({emitEvent: false});
    }
  }

  // genera los tokens
  generateTokens() {
    if (this.formGenerateTokens.invalid) {
      this.snackbarService.showError('El número de tokens no es válido.');
      return;
    }
    this.isLoading = true;
    const tokensValue = this.formGenerateTokens.get('numberOfTokens')?.value ?? 0;
    this.campanasService.generateTokensCampain(this.campaing.publicId, tokensValue).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.snackbarService.showSuccess('Los tokens de la campaña se crearón con éxito ');
        const tokens = response.data?.tokens || response || [];
        this.tokens = Array.isArray(tokens) ? tokens.map((token: any) => typeof token === 'string' ? { accessToken: token, status: 'PENDING' } : token) : [];
        this.stepGeneratedTokens = true;
        this.flujoCompletado = true;
        this.updateHeader();
      },
      error: (err) => {
        this.snackbarService.showError('Error al generar los tokens de la campaña ' + err.message);
        this.isLoading = false;
      }
    });
  }

  generateTokensFromEdit() {
    // no permitir generar si la campaña ya inició (incluye hoy) o terminó
/*    if (this.isLocked) {
      this.snackbarService.showError('La campaña ya comenzó o finalizó; no es posible generar tokens.');
      return;
    }*/

    const numberCtrl = this.formGenerateTokens.get('numberOfTokens');
    if (!numberCtrl || numberCtrl.invalid) {
      this.snackbarService.showError('El número de tokens no es válido.');
      numberCtrl?.markAsTouched();
      return;
    }

    const publicId = this.campaingToEdit?.publicId ?? '';
    const count = Number(numberCtrl.value ?? 0);
    if (!publicId || count <= 0) return;

    this.isLoading = true;
    this.campanasService.generateTokensCampain(publicId, count).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.snackbarService.showSuccess('Tokens generados correctamente');
        const tokens = response?.data?.tokens || response || [];
        this.tokens = Array.isArray(tokens) ? tokens.map((token: any) => typeof token === 'string' ? { accessToken: token, status: 'PENDING' } : token) : [];
        this.stepGeneratedTokens = true;
        this.flujoCompletado = true;
        this.updateHeader();

        const numberCtrl = this.formGenerateTokens.get('numberOfTokens') as FormControl<number>;
        numberCtrl.setValue(this.tokens.length as any);
        numberCtrl.disable({emitEvent: false});

        // ahora que ya hay tokens, ya no debe poder generar más
        this.canGenerateTokens = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.snackbarService.showError('Error al generar los tokens de la campaña ' + (err?.message ?? ''));
      }
    });
  }

  private obtenerTokensContestados(campaignId: string) {
    return this.surveyResponses
      .getSurveySubmissionSummaries({campaign: campaignId, page: 0, size: 1})
      .pipe(
        switchMap((primera) => {
          const total = primera?.totalElements ?? 0;
          if (total === 0) {
            return of(new Set<string>());
          }
          return this.surveyResponses
            .getSurveySubmissionSummaries({campaign: campaignId, page: 0, size: total})
            .pipe(map((todas) => new Set((todas?.content ?? []).map((e) => e.token))));
        })
      );
  }

  // Modifica la función 'getTokens'
  getTokens() {
    if (!this.campaingToEdit) {
      return;
    }
    this.isLoading = true;
    const campaignId = this.campaingToEdit.publicId ?? '';
    forkJoin({
      tokens: this.campanasService.getTokensCampain(campaignId),
      contestados: this.obtenerTokensContestados(campaignId),
    }).subscribe({
      next: ({tokens: response, contestados}) => {
        this.isLoading = false;

        this.tokensContestados = contestados;
        this.tokens = response; // Almacena el array completo de objetos
        const hasTokens = Array.isArray(this.tokens) && this.tokens.length > 0;

        if (hasTokens) {
          this.snackbarService.showSuccess('Tokens de la campaña obtenidos con éxito ');
          this.showEmptyTokensModal = false;
        } else {
          this.showEmptyTokensModal = true;
        }

        this.step = 3;
        this.stepGeneratedTokens = true;
        this.flujoCompletado = true;
        this.updateHeader();

        this.formGenerateTokens.patchValue({
          nameCampain: this.campaingToEdit?.name ?? '',
          numberOfTokens: this.campaingToEdit?.tokensToGenerate ?? 0,
        });

        const min = Number(this.formCampaing.get('minimumResponses')?.value ?? 0);
        const max = Number(this.formCampaing.get('totalEmployees')?.value ?? 0);
        const numberCtrl = this.formGenerateTokens.get('numberOfTokens') as FormControl<number>;

        if (this.isLocked || hasTokens) {
          numberCtrl.setValue(
            hasTokens
              ? (this.tokens.length as any)
              : (this.campaingToEdit?.tokensToGenerate ?? 0 as any),
            {emitEvent: false}
          );
          numberCtrl.disable({emitEvent: false});
          this.canGenerateTokens = false;
        } else {
          numberCtrl.enable({emitEvent: false});
          numberCtrl.setValidators([
            Validators.required,
            Validators.min(Math.max(min, 1)),
            Validators.max(max),
          ]);
          numberCtrl.updateValueAndValidity({emitEvent: false});
          const target = Number(this.campaingToEdit?.tokensToGenerate ?? min);
          const initial = Math.min(Math.max(target, Math.max(min, 1)), max);
          numberCtrl.setValue(initial as any, {emitEvent: false});

          this.canGenerateTokens = true;
        }
      },
      error: (err) => {
        this.snackbarService.showError('Error al obtener los tokens de la campaña ' + err.message);
        this.isLoading = false;
      }
    });
  }

  onConfirmGenerateTokens() {
    this.showEmptyTokensModal = false;
    this.generateTokensFromEdit();
  }

  onCancel() {
    this.showEmptyTokensModal = false; // Cierra el modal
  }

  goBack(): void {
    this.router.navigate(['/clientes/campañas']);
  }


  //actualiza los datos del header
  updateHeader() {
    if (this.stepGeneratedTokens) {
      this.pageData.set({
        title: AppConstants.CAMPANAS_TITLE_GENERATED_TOKENS,
        subTitle: AppConstants.CAMPANAS_SUBTITLE_GENERATED_TOKENS
      });
    } else {
      this.pageData.set({
        title: AppConstants.CAMPANAS_TITLE_TOKEN,
        subTitle: AppConstants.CAMPANAS_SUBTITLE_TOKEN
      });
    }
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
    });
  }

  addFormValidations() {
    this.formCampaing.get('startDate')!.valueChanges.subscribe(() => this.validarFechas());
    this.formCampaing.get('endDate')!.valueChanges.subscribe(() => this.validarFechas())

    //calculo del minimo de respuestas y tokens para generar
    const totalEmployeesControl = this.formCampaing.get('totalEmployees');
    if (totalEmployeesControl) {
      totalEmployeesControl.valueChanges.subscribe((value) => {
        const total = Number(value);
        const minResponses = this.formCampaing.get('minimumResponses');
        const tokensToGenerate = this.formCampaing.get('tokensToGenerate');

        if (minResponses) {
          if (total > 0) {
            let result: number;
            if (total <= 50) {
              result = total;
            } else {
              const numerator = 0.9604 * total;
              const denominator = 0.0025 * (total - 1) + 0.9604;
              result = Math.ceil(numerator / denominator);
            }
            minResponses.setValue(result);
          }
        }

        // Rellenar tokens para generar con el total de empleados
        if (tokensToGenerate && !tokensToGenerate.disabled) {
          tokensToGenerate.setValue(total > 0 ? total.toString() : '');
        }
      });
    }
  }

  //valida que las fecha de fin no sea igual menor que la de inicio
  validarFechas() {
    const startControl = this.formCampaing.get('startDate');
    const endControl = this.formCampaing.get('endDate');

    if (!startControl || !endControl) return;

    const start = startControl.value;
    const end = endControl.value;

    // Obtenemos errores existentes sin sobrescribir
    const endErrors = endControl.errors ? {...endControl.errors} : null;

    // Validación de rango
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);

      if (endDate <= startDate) {
        endControl.setErrors({...endErrors, invalidRange: true});
      } else {
        if (endErrors) {
          delete endErrors['invalidRange'];
          endControl.setErrors(Object.keys(endErrors).length ? endErrors : null);
        }
      }
    } else {
      if (endErrors) {
        delete endErrors['invalidRange'];
        endControl.setErrors(Object.keys(endErrors).length ? endErrors : null);
      }
    }
  }

  tokensExceedTotalEmployees: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const total = group.get('totalEmployees')?.value;
    const tokens = group.get('tokensToGenerate')?.value;
    const minResponses = group.get('minimumResponses')?.value;
    const errors: any = {};
    if (total != null && tokens != null && tokens > total) {
      return {tokensExcedenEmpleados: true};
    }
    if (minResponses != null && tokens != null && tokens < minResponses) {
      errors.tokensMenoresQueMinimo = true;
    }
    return Object.keys(errors).length > 0 ? errors : null;
  };

  formCampaing = this.formBuilder.group({
    name: new FormControl('', [Validators.required]),
    description: new FormControl(''),
    surveyUrl: new FormControl(''),
    startDate: new FormControl('', [Validators.required]),
    endDate: new FormControl('', [Validators.required]),
    totalEmployees: new FormControl('', [Validators.required, Validators.min(1)]),
    minimumResponses: new FormControl({value: 0, disabled: true}),
    tokensToGenerate: new FormControl('', [Validators.required, Validators.min(1)]),
  }, {validators: this.tokensExceedTotalEmployees});

  exportTokensToCSV() {
    if (!this.tokens || this.tokens.length === 0) {
      alert('No hay tokens para exportar');
      return;
    }

    const header = 'Número,Token\n';
    const rows = this.tokens
      .map((token: any, index: number) => `${index + 1},${token.accessToken}`)
      .join('\n');

    const csvContent = header + rows;

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');

    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'tokens.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportTokensToExcel(): void {
    if (!this.tokens || this.tokens.length === 0) {
      alert('No hay tokens para exportar');
      return;
    }

    // Preparamos los datos (incluyendo encabezados)
    const data = [
      ['Número', 'Token'], // Encabezados
      ...this.tokens.map((token: any, index) => [index + 1, token.accessToken]),
    ];

    // Creamos la hoja
    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);

    // Creamos el libro
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tokens');

    // Generamos el archivo Excel en binario
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    // Creamos un blob y lo descargamos
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'tokens.xlsx');
  }

  private addDays(d: Date, days = 1): Date {
    // normaliza a 00:00 local y suma días
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    x.setDate(x.getDate() + days);
    return x;
  }

  private toISODate(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  protected getStatus(tokenObj: { accessToken?: string; status?: string }): string {
    const today = toDateOnly(new Date());
    const campaignEndDate = this.campaingToEdit ? toDateOnly(this.campaingToEdit.endDate) : null;

    if (tokenObj?.accessToken && this.tokensContestados.has(tokenObj.accessToken)) {
      return 'Inactivo';
    }

    if (campaignEndDate && today > campaignEndDate) {
      return 'Inactivo';
    }

    const statusMap: { [key: string]: string } = {
      'PENDING': 'Activo',
      'ACTIVE': 'Activo',
      'COMPLETED': 'Inactivo',
      'EXPIRED': 'Inactivo',
      'REVOKED': 'Inactivo'
    };
    return statusMap[tokenObj?.status ?? ''] ?? 'Inactivo';
  }
}
